import { readonly, ref, onMounted, onBeforeUnmount } from 'vue'

const CONSENT_EVENT = 'UC_UI_CMP_EVENT'
const POLL_INTERVAL_MS = 300
const MAX_POLL_ATTEMPTS = 10

interface UCService {
  name: string
  consent: { status: boolean }
}

interface UCUI {
  getServicesBaseInfo(): UCService[] | Promise<UCService[]>
}

function getUCUI(): UCUI | undefined {
  return typeof window !== 'undefined' ? (window as any).UC_UI : undefined
}

/**
 * Checks whether the user has given Usercentrics consent for a service.
 * Matches against the service's display name (case-insensitive).
 *
 * The CMP script loads asynchronously, so we poll until UC_UI is available.
 * After that, consent changes are picked up via the UC_UI_CMP_EVENT event.
 *
 * @param serviceName - Display name in Usercentrics (e.g. "Odoscope", "Google Analytics")
 */
export function useServiceConsent(serviceName: string) {
  const hasConsent = ref(false)

  let disposed = false
  let pollTimeoutId: ReturnType<typeof setTimeout> | null = null

  const normalizedName = serviceName.trim().toLowerCase()

  async function syncConsent(): Promise<boolean> {
    try {
      const raw = getUCUI()?.getServicesBaseInfo?.()
      if (raw === undefined) {
        return false
      }

      const services = await Promise.resolve(raw)
      if (!Array.isArray(services)) {
        return false
      }

      const service = services.find(
        (s) => s?.name?.trim().toLowerCase() === normalizedName,
      )
      if (!disposed) {
        hasConsent.value = Boolean(service?.consent?.status)
      }
      return true
    } catch {
      return false
    }
  }

  async function pollUntilReady(attempt = 0) {
    if (disposed) {
      return
    }
    if (await syncConsent()) {
      return
    }
    if (disposed || attempt >= MAX_POLL_ATTEMPTS) {
      return
    }

    pollTimeoutId = setTimeout(
      () => void pollUntilReady(attempt + 1),
      POLL_INTERVAL_MS,
    )
  }

  function onConsentChange() {
    void syncConsent()
  }

  onMounted(() => {
    void pollUntilReady()
    window.addEventListener(CONSENT_EVENT, onConsentChange)
  })

  onBeforeUnmount(() => {
    disposed = true
    if (pollTimeoutId != null) {
      clearTimeout(pollTimeoutId)
    }
    window.removeEventListener(CONSENT_EVENT, onConsentChange)
  })

  return { hasConsent: readonly(hasConsent) }
}
